/**
 * dashboardTips — Curated "Did you know?" tips shown one-per-day on the
 * dashboard. Selection is personalized: tips for features a user already
 * uses heavily are skipped in favor of features they've never touched.
 * Falls back to deterministic day-of-year rotation if no candidates apply.
 */

export type TipAction =
  | { type: "route"; value: string }
  | { type: "event"; value: string }
  | { type: "route+event"; route: string; event: string };

export interface DashboardTip {
  id: string;
  eyebrow: string;
  body: string;
  ctaLabel: string;
  action: TipAction;
  /** Skip this tip when the predicate returns true (user already engaged). */
  skipWhen?: (ctx: TipContext) => boolean;
}

/** Context provided to the personalization layer. */
export interface TipContext {
  streakDays: number;
  hasCertificate: boolean;
  hasWishlist: boolean;
  hasConnection: boolean;
  hasReflectedToday: boolean;
  togetherCount: number;
}

export const DASHBOARD_TIPS: DashboardTip[] = [
  {
    id: "community-goals",
    eyebrow: "Did you know?",
    body: "You can join a Community Goal and earn extra points for showing up.",
    ctaLabel: "Show me",
    action: { type: "route", value: "/common-goals" },
  },
  {
    id: "todo-list",
    eyebrow: "Did you know?",
    body: "Track today's small wins — tap to open your to-do list.",
    ctaLabel: "Open list",
    action: { type: "event", value: "open-quick-tasks" },
  },
  {
    id: "together-time",
    eyebrow: "Did you know?",
    body: "Together Time is your ready-made happiness checklist — six small things families forget to put on a to-do list.",
    ctaLabel: "Pick one",
    action: { type: "event", value: "open-together-time" },
    skipWhen: (c) => c.togetherCount > 0,
  },
  {
    id: "certificates",
    eyebrow: "Did you know?",
    body: "Completed a goal? Generate a certificate to celebrate.",
    ctaLabel: "View certificates",
    action: { type: "route", value: "/certificates" },
    skipWhen: (c) => c.hasCertificate,
  },
  {
    id: "wishlist",
    eyebrow: "Did you know?",
    body: "Share your wishlist so friends know how to support you.",
    ctaLabel: "Open wishlist",
    action: { type: "route", value: "/wishlist" },
    skipWhen: (c) => c.hasWishlist,
  },
  {
    id: "connections",
    eyebrow: "Did you know?",
    body: "Invite a friend to pledge support on one of your goals.",
    ctaLabel: "See connections",
    action: { type: "route", value: "/connections" },
    skipWhen: (c) => c.hasConnection,
  },
  {
    id: "marketplace",
    eyebrow: "Did you know?",
    body: "Check the marketplace for daily offers from UAE partners.",
    ctaLabel: "Browse offers",
    action: { type: "route", value: "/marketplace" },
  },
  {
    id: "journal",
    eyebrow: "Did you know?",
    body: "Log what you did in a Community Goal — +1 pt each, +3 for your first this week.",
    ctaLabel: "Show me",
    action: { type: "route", value: "/common-goals" },
  },
  {
    id: "rewards-hub",
    eyebrow: "Did you know?",
    body: "Spend your points on cosmetics and power-ups in the Rewards Hub.",
    ctaLabel: "Open Rewards",
    action: { type: "route", value: "/rewards" },
  },
  {
    id: "my-journey",
    eyebrow: "Did you know?",
    body: "See all your points, level and unlocked rewards in one place.",
    ctaLabel: "My Journey",
    action: { type: "route", value: "/my-progress" },
  },
  {
    id: "arcade",
    eyebrow: "Did you know?",
    body: "Play UAE Treasure Hunt or Memory Match in the Rewards Arcade to win perks.",
    ctaLabel: "Open Arcade",
    action: { type: "route", value: "/rewards" },
  },
];

const DISABLED_KEY = "gt_tips_disabled";
const DISMISSED_PREFIX = "gt_tips_dismissed:";
const SHOWN_PREFIX = "gt_tips_shown:"; // per-tip-id last shown date
const LAST_GLOBAL_SHOWN_KEY = "gt_tips_last_global_shown_at";
const QUIET_HOURS_START = 9;
const QUIET_HOURS_END = 21;
const MIN_HOURS_BETWEEN_TIPS = 72; // throttle: at most once every 72h

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayOfYear(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime();
  return Math.floor(diff / 86_400_000);
}

export function areTipsDisabled(): boolean {
  try {
    return localStorage.getItem(DISABLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function disableTips(): void {
  try {
    localStorage.setItem(DISABLED_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function isDismissedToday(): boolean {
  try {
    return localStorage.getItem(DISMISSED_PREFIX + todayKey()) === "1";
  } catch {
    return false;
  }
}

export function dismissForToday(): void {
  try {
    localStorage.setItem(DISMISSED_PREFIX + todayKey(), "1");
    localStorage.setItem(LAST_GLOBAL_SHOWN_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/** True if a tip was shown within the last MIN_HOURS_BETWEEN_TIPS. */
export function isThrottled(): boolean {
  try {
    const raw = localStorage.getItem(LAST_GLOBAL_SHOWN_KEY);
    if (!raw) return false;
    const last = Number(raw) || 0;
    const hoursSince = (Date.now() - last) / 3_600_000;
    return hoursSince < MIN_HOURS_BETWEEN_TIPS;
  } catch {
    return false;
  }
}

/** Quiet hours: don't pop tips before 9 am or after 9 pm local time. */
export function isInQuietHours(d = new Date()): boolean {
  const h = d.getHours();
  return h < QUIET_HOURS_START || h >= QUIET_HOURS_END;
}

function lastShown(id: string): number {
  try {
    const raw = localStorage.getItem(SHOWN_PREFIX + id);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
}

function markShown(id: string) {
  try {
    localStorage.setItem(SHOWN_PREFIX + id, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/**
 * Pick the best tip for the current user. Strategy:
 *  1. Filter out tips the user has clearly outgrown (skipWhen).
 *  2. Prefer tips never shown to this user.
 *  3. Then prefer the least-recently shown tip.
 *  4. Fall back to deterministic day-of-year rotation.
 */
export function getTodaysTip(ctx?: Partial<TipContext>): DashboardTip {
  const fullCtx: TipContext = {
    streakDays: ctx?.streakDays ?? 0,
    hasCertificate: ctx?.hasCertificate ?? false,
    hasWishlist: ctx?.hasWishlist ?? false,
    hasConnection: ctx?.hasConnection ?? false,
    hasReflectedToday: ctx?.hasReflectedToday ?? false,
    togetherCount: ctx?.togetherCount ?? 0,
  };

  const candidates = DASHBOARD_TIPS.filter((t) => !t.skipWhen || !t.skipWhen(fullCtx));
  if (candidates.length === 0) {
    return DASHBOARD_TIPS[dayOfYear() % DASHBOARD_TIPS.length];
  }

  candidates.sort((a, b) => lastShown(a.id) - lastShown(b.id));
  const pick = candidates[0];
  markShown(pick.id);
  return pick;
}
